import { ChangeEvent, FormEvent, useEffect, useMemo, useState } from 'react';

// SURELINC_REWRITE_2026_06_11_WORKSPACE_SUBMISSION_LOCK: rewritten from uploaded source file; rename back to ClientEvidenceWorkspacePage.tsx when applying.
import { Link, useParams } from 'react-router-dom';
import { createHttpClient } from '@shared/api/httpClient';
import { canDownloadClientArtifacts } from '@shared/auth/cognito';
import { ApiError } from '@shared/types/api';
import type {
  ClientAttestationRequest,
  ClientAttestationResponse,
  CompletedDocumentUploadResponse,
  DocumentStatusUpdateRequest,
  DocumentStatusUpdateResponse,
  EvidenceWorkspaceDocument,
  EvidenceWorkspaceDocumentsResponse,
  EvidenceWorkspacePackage,
  EvidenceWorkspacePackageResponse,
  EvidenceWorkspacePackagesResponse,
  PreparedDocumentDownloadResponse,
  PreparedDocumentsDownloadAllResponse,
  SubmitEvidenceWorkspaceResponse,
} from '@shared/types/api';
import { formatLocalDateTime } from '@shared/utils/dateTime';
import { statusPillClassName } from '@shared/utils/statusPill';
import { useClientAiAssistant } from '../components/ClientAiAssistantDrawer';
import { useClientAccount } from '../account/AccountContext';
import { CMMC_LEVEL_OPTIONS, DEFAULT_GRID_LIMIT, GRID_LIMIT_OPTIONS, metadataFromResponse, type GridMetadata, type SearchMode } from './gridUi';
import {
  ApiRecord,
  asArray,
  asRecord,
  friendlyStatus,
  recordFromResponse,
  recordsFromResponse,
  text,
  valueOrDash,
} from './packageUi';

type LoadState = {
  loading: boolean;
  packages: EvidenceWorkspacePackage[];
  workspacePackage?: EvidenceWorkspacePackage;
  documents: EvidenceWorkspaceDocument[];
  error?: string;
};

type OperationState = {
  documentId?: string;
  kind: 'idle' | 'loading' | 'success' | 'warning' | 'error';
  message?: string;
};

type RowSaveStatus = 'idle' | 'dirty' | 'saving' | 'saved' | 'error';

type WorkspaceValidationIssue = {
  documentId?: string;
  field?: string;
  message: string;
};

interface WorkspaceSearchState {
  search: string;
  searchMode: SearchMode;
  status: string;
  cmmcLevel: string;
  limit: number;
}

const WORKSPACE_STATUS_OPTIONS = [
  '',
  'PACKAGE_INDEXED',
  'CLIENT_ATTESTED',
  'COMPLETED_EVIDENCE_PACKAGE_UPLOADED_TO_INTAKE',
  'SUBMITTED_FOR_FINAL_AUDITOR_PACKAGE',
  'FINAL_AUDITOR_PACKAGE_REQUESTED',
  'SUPERSEDED',
  'FAILED',
];



const DECLARATION_DOCUMENT_IDS = new Set([
  'conditional-technology-applicability-attestation',
  'identity-provider-password-lifecycle-attestation',
]);

const DECLARATION_OUTCOME_OPTIONS = [
  'NOT_APPLICABLE',
  'APPLICABLE_AND_IMPLEMENTED',
  'APPLICABLE_BUT_NEEDS_REMEDIATION',
];

const APPROVED_BY_OPTIONS = [
  'Client Executive Sponsor',
  'Client Security Owner',
  'Client Compliance Lead',
  'Client IT/System Owner',
  'Client HR/Personnel Owner',
  'Client Facilities/Physical Security Owner',
  'Client Data Owner',
  'SURELINC Reviewer',
  'Not Applicable',
  'Other',
];

const REQUIRED_FIELD_LABELS: Record<string, string> = {
  uploadedOriginalFileName: 'Completed upload',
  clientValidatedComplete: 'Validated',
  signed: 'Signed',
  approvedBy: 'Approved by',
  approvalDate: 'Approval date',
  intendedOutcome: 'Declared outcome',
};

function requiredLabel(label: string) {
  return (
    <>
      {label} <span className="required-marker" aria-label="required">*</span>
    </>
  );
}

function evidenceWorkspacePackageId(pkg: EvidenceWorkspacePackage): string {
  return String(pkg.packageId || pkg.id || pkg.packageName || '');
}

function evidenceWorkspacePackageName(pkg: EvidenceWorkspacePackage): string {
  return String(pkg.packageName || pkg.packageId || 'Evidence workspace');
}

function shouldShowWorkspacePackageId(pkg: EvidenceWorkspacePackage): boolean {
  const id = evidenceWorkspacePackageId(pkg);
  const name = evidenceWorkspacePackageName(pkg);
  return Boolean(id && id !== name);
}

function evidenceWorkspaceStatus(pkg?: EvidenceWorkspacePackage): string {
  return friendlyStatus(pkg?.workspaceStatus || pkg?.packageStatus || pkg?.status);
}


function isFinalAuditorPackage(pkg: EvidenceWorkspacePackage): boolean {
  const packageType = String(pkg.packageType || '').toUpperCase();
  const packageStage = String(pkg.packageStage || '').toUpperCase();
  const statusValues = [pkg.workspaceStatus, pkg.packageStatus, pkg.status, pkg.packageLifecycleStatus, pkg.jobStatus]
    .map((value) => String(value || '').toUpperCase());
  const id = evidenceWorkspacePackageId(pkg).toUpperCase();
  const name = evidenceWorkspacePackageName(pkg).toUpperCase();
  return (
    packageType === 'FINAL_AUDITOR_PACKAGE' ||
    packageStage === 'FINAL_AUDITOR_READY' ||
    statusValues.includes('AUDITOR_PACKAGE_READY') ||
    Boolean(pkg.finalAuditorPackageKey || pkg.finalAuditorPackageS3Uri) ||
    id.includes('AUDITOR-EVIDENCE-PACKAGE') ||
    name.includes('AUDITOR-EVIDENCE-PACKAGE')
  );
}

function isEditableWorkspacePackage(pkg: EvidenceWorkspacePackage): boolean {
  return !isFinalAuditorPackage(pkg);
}

const WORKSPACE_LOCKING_STATUSES = new Set([
  'COMPLETED_EVIDENCE_PACKAGE_BUILD_REQUESTED',
  'COMPLETED_EVIDENCE_PACKAGE_BUILD_IN_PROGRESS',
  'COMPLETED_EVIDENCE_PACKAGE_UPLOADED_TO_INTAKE',
  'SUBMITTED_FOR_FINAL_AUDITOR_PACKAGE',
  'FINAL_AUDITOR_PACKAGE_REQUESTED',
  'FINAL_AUDITOR_PACKAGE_GENERATION_REQUESTED',
  'FINAL_AUDITOR_PACKAGE_GENERATION_IN_PROGRESS',
  'FINAL_ASSESSOR_REVIEW_PENDING',
  'AUDITOR_PACKAGE_READY',
  'FINAL_AUDITOR_READY',
  'FINAL_AUDITOR_PACKAGE_READY',
  'PROCESSED_FINAL_PACKAGE_READY',
]);

function workspaceStatusValues(pkg?: EvidenceWorkspacePackage): string[] {
  if (!pkg) return [];
  return [pkg.workspaceStatus, pkg.packageStatus, pkg.status, pkg.packageLifecycleStatus, pkg.jobStatus, pkg.displayStatus]
    .map((value) => String(value || '').trim().toUpperCase())
    .filter(Boolean);
}

function isWorkspaceLocked(pkg?: EvidenceWorkspacePackage): boolean {
  if (!pkg) return false;
  if (typeof pkg.workspaceLocked === 'boolean') return pkg.workspaceLocked;
  return Boolean(
    pkg.relatedFinalPackageId ||
    pkg.finalAuditorPackageKey ||
    workspaceStatusValues(pkg).some((status) => WORKSPACE_LOCKING_STATUSES.has(status)),
  );
}

function isWorkspaceBuildFailed(pkg?: EvidenceWorkspacePackage): boolean {
  return workspaceStatusValues(pkg).some((status) => status === 'COMPLETED_EVIDENCE_PACKAGE_BUILD_FAILED');
}

type WorkspaceBuildFailureGuidance = {
  message: string;
  contactSupport: boolean;
};

const USER_CORRECTABLE_BUILD_FAILURE_PATTERNS = [
  /unsupported (?:file|document|format|type)/i,
  /invalid (?:file|document|format|type)/i,
  /corrupt(?:ed)? (?:file|document)/i,
  /password[- ]protected/i,
  /encrypted (?:file|document)/i,
  /file (?:is )?too large/i,
  /exceeds? (?:the )?(?:maximum )?(?:file )?size/i,
  /missing required (?:file|document|field)/i,
  /required (?:file|document|field) (?:is )?missing/i,
  /unable to (?:open|read|convert) (?:the )?(?:uploaded )?(?:file|document)/i,
  /requires an explicit intendedOutcome/i,
  /applicability outcome/i,
];

function workspaceBuildFailureGuidance(pkg?: EvidenceWorkspacePackage): WorkspaceBuildFailureGuidance {
  const failureReason = String(pkg?.workspaceBuildFailureReason || '').trim();
  const userCorrectable = USER_CORRECTABLE_BUILD_FAILURE_PATTERNS.some((pattern) => pattern.test(failureReason));

  if (userCorrectable) {
    return {
      message: 'One or more uploaded documents could not be processed. Review the uploaded files, replace any invalid, damaged, encrypted, or unsupported documents, and submit the package again.',
      contactSupport: false,
    };
  }

  return {
    message: 'The completed evidence package could not be generated because of a processing error. Your saved evidence is still available.',
    contactSupport: true,
  };
}

function workspaceClosedMessage(pkg?: EvidenceWorkspacePackage): string {
  if (workspaceStatusValues(pkg).some((status) => status === 'COMPLETED_EVIDENCE_PACKAGE_BUILD_REQUESTED' || status === 'COMPLETED_EVIDENCE_PACKAGE_BUILD_IN_PROGRESS' || status === 'FINAL_AUDITOR_PACKAGE_REQUESTED' || status === 'FINAL_AUDITOR_PACKAGE_GENERATION_REQUESTED' || status === 'FINAL_AUDITOR_PACKAGE_GENERATION_IN_PROGRESS' || status === 'SUBMITTED_FOR_FINAL_AUDITOR_PACKAGE')) {
    return 'This workspace is locked because the completed evidence package has been submitted and final auditor package generation has been requested.';
  }
  const relatedName = String(pkg?.relatedFinalPackageName || pkg?.relatedFinalPackageId || 'the final auditor package');
  return `This workspace is closed because ${relatedName} has already been generated. Start a new evidence workflow to make additional changes.`;
}

function isEditableWorkspaceDocument(document: EvidenceWorkspaceDocument): boolean {
  const documentType = String(document.documentType || '').toUpperCase();
  const documentId = String(document.documentId || '').toUpperCase();
  const documentName = String(document.documentName || document.uploadedOriginalFileName || '').toUpperCase();
  const documentKey = String(document.documentKey || '').toUpperCase();
  return !(
    documentType === 'FINAL_AUDITOR_PACKAGE' ||
    documentId.startsWith('FINAL_AUDITOR_PACKAGE#') ||
    documentId.includes('FINAL_AUDITOR_PACKAGE#') ||
    documentName.includes('AUDITOR-EVIDENCE-PACKAGE') ||
    documentKey.includes('FINAL-AUDITOR-PACKAGES/')
  );
}

const DOCUMENT_LABEL_ACRONYMS: Record<string, string> = {
  cui: 'CUI',
  mfa: 'MFA',
  poam: 'POA&M',
  ssp: 'SSP',
};

const DOCUMENT_LABEL_LOWERCASE_WORDS = new Set(['and', 'for', 'of', 'or', 'the', 'to']);

function humanizeDocumentLabel(value: unknown): string {
  const raw = String(value || '').trim();
  if (!raw) return '';

  const fileName = raw.split(/[\\/]/).pop() || raw;
  const withoutExtension = fileName.replace(/\.(?:docx?|xlsx?|pdf|md)$/i, '');
  const withoutCmmcPrefix = withoutExtension.replace(/^cmmc[-_\s]+/i, '');
  const words = withoutCmmcPrefix
    .replace(/[-_]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .split(' ')
    .filter(Boolean);

  return words
    .map((word, index) => {
      const normalized = word.toLowerCase();
      if (DOCUMENT_LABEL_ACRONYMS[normalized]) return DOCUMENT_LABEL_ACRONYMS[normalized];
      if (index > 0 && DOCUMENT_LABEL_LOWERCASE_WORDS.has(normalized)) return normalized;
      return `${normalized.charAt(0).toUpperCase()}${normalized.slice(1)}`;
    })
    .join(' ');
}

function documentDisplayName(document: EvidenceWorkspaceDocument): string {
  const record = document as ApiRecord;
  const explicitDisplayName = text(record, 'displayName', 'documentDisplayName', 'documentTitle', 'title');
  if (explicitDisplayName) return explicitDisplayName.replace(/^CMMC[-_\s]+/i, '').trim();

  return (
    humanizeDocumentLabel(
      document.documentName ||
        document.preparedDocumentFileName ||
        document.uploadedOriginalFileName ||
        document.convertedPdfFileName ||
        document.documentId,
    ) || 'Evidence Document'
  );
}

function completedUploadLabel(document: EvidenceWorkspaceDocument): string {
  return hasCompletedUpload(document) ? 'Uploaded' : 'Pending upload';
}

function buildDocumentAiPrompt(document: EvidenceWorkspaceDocument, displayName: string): string {
  const record = document as ApiRecord;
  const documentIdText = String(document.documentId || '').trim();
  const preparedFile = String(document.preparedDocumentFileName || '').trim();
  const controlIdText = text(record, 'controlId', 'controlIdentifier', 'cmmcControlId', 'practiceId');
  const controlTitleText = text(record, 'controlTitle', 'practiceTitle', 'title');
  const statusText = text(record, 'status', 'documentStatus', 'workspaceStatus', 'clientEvidenceStatus');
  const reasonText = text(record, 'reason', 'reasonAction', 'action', 'recommendedAction', 'controlReasonAction');
  const parts = [
    `Explain the Evidence Workspace document ${displayName}.`,
    'Answer only about this selected document. Cover its likely purpose, why it matters, who should complete/review/sign it when applicable, what customer-specific facts or fields to verify, supporting evidence to gather, and the next safe completion step.',
  ];
  if (controlIdText) {
    parts.push(`Related control: ${controlIdText}${controlTitleText ? ` - ${controlTitleText}` : ''}.`);
  }
  if (statusText) {
    parts.push(`Current document status: ${statusText}.`);
  }
  if (reasonText) {
    parts.push(`Current reason or action: ${reasonText}.`);
  }
  if (documentIdText && documentIdText !== displayName) {
    parts.push(`Document ID: ${documentIdText}.`);
  }
  if (preparedFile && preparedFile !== displayName) {
    parts.push(`Prepared file: ${preparedFile}.`);
  }
  parts.push('Keep the answer client-safe. Do not expose internal platform details. Do not perform any portal actions.');
  return parts.join(' ');
}

function statusClass(value: unknown): string {
  return statusPillClassName(value);
}

function normalizeWorkspacePackages(response: unknown): EvidenceWorkspacePackage[] {
  const typed = response as EvidenceWorkspacePackagesResponse;
  const packages = Array.isArray(typed.workspacePackages)
    ? typed.workspacePackages
    : recordsFromResponse(response, ['workspacePackages', 'packages', 'items']).map(
        (item) => item as EvidenceWorkspacePackage,
      );
  return packages.filter(isEditableWorkspacePackage);
}

function normalizeWorkspacePackage(response: unknown): EvidenceWorkspacePackage {
  const typed = response as EvidenceWorkspacePackageResponse;
  const envelope = asRecord(response);
  const packageRecord = (typed.workspacePackage || recordFromResponse(response, ['workspacePackage', 'package', 'item'])) as EvidenceWorkspacePackage;
  const summary = asRecord(envelope.summary);
  const attestation = asRecord(envelope.attestation) || asRecord(packageRecord.attestation);

  return {
    ...packageRecord,
    ...(Object.keys(summary).length > 0 ? { summary } : {}),
    ...(Object.keys(attestation).length > 0 ? { attestation } : {}),
  } as EvidenceWorkspacePackage;
}

function normalizeWorkspaceDocuments(response: unknown): EvidenceWorkspaceDocument[] {
  const typed = response as EvidenceWorkspaceDocumentsResponse;
  const documents = Array.isArray(typed.documents)
    ? typed.documents
    : recordsFromResponse(response, ['documents', 'items']).map((item) => item as EvidenceWorkspaceDocument);
  return documents.filter(isEditableWorkspaceDocument);
}

function attestationDraftFromPackage(pkg: EvidenceWorkspacePackage, packageId: string): ClientAttestationRequest | undefined {
  const attestationRecord = asRecord(pkg.attestation);
  if (Object.keys(attestationRecord).length === 0) return undefined;

  return {
    packageId,
    fullName: text(attestationRecord, 'signerName') || '',
    title: text(attestationRecord, 'signerTitle') || '',
    company: text(attestationRecord, 'company') || '',
    email: text(attestationRecord, 'signerEmail') || '',
    typedSignature: text(attestationRecord, 'typedSignature') || '',
    notes: text(attestationRecord, 'notes') || '',
    certificationAccepted: Boolean(attestationRecord.accepted),
  };
}

function metricValue(pkg: EvidenceWorkspacePackage | undefined, key: string, fallback = 0): number {
  const summary = asRecord(pkg?.summary);
  const value = summary[key] ?? (pkg as ApiRecord | undefined)?.[key];
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim()) {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return fallback;
}


function hasCompletedUpload(document: EvidenceWorkspaceDocument): boolean {
  return Boolean(document.uploadedOriginalFileName || document.uploadedOriginalKey || document.convertedPdfKey);
}

function documentAllowedOutcomes(document: EvidenceWorkspaceDocument): string[] {
  const configured = Array.isArray(document.allowedOutcomes)
    ? document.allowedOutcomes.map((value) => String(value || '').trim().toUpperCase()).filter(Boolean)
    : [];
  const documentId = String(document.documentId || '').trim();
  if (DECLARATION_DOCUMENT_IDS.has(documentId)) {
    return DECLARATION_OUTCOME_OPTIONS;
  }
  return Array.from(new Set(configured));
}

function documentRequiresExplicitOutcome(document: EvidenceWorkspaceDocument): boolean {
  const documentId = String(document.documentId || '').trim();
  const allowed = documentAllowedOutcomes(document);
  return DECLARATION_DOCUMENT_IDS.has(documentId)
    || (allowed.length > 0 && !allowed.includes('PASS') && allowed.every((value) => DECLARATION_OUTCOME_OPTIONS.includes(value)));
}

function documentDraftOutcome(document: EvidenceWorkspaceDocument): string {
  const current = String(document.intendedOutcome || '').trim().toUpperCase();
  const allowed = documentAllowedOutcomes(document);
  if (current && (allowed.length === 0 || allowed.includes(current))) return current;

  if (documentRequiresExplicitOutcome(document)) return '';

  return allowed.length === 0 || allowed.includes('PASS') ? 'PASS' : (allowed[0] || 'PASS');
}


function draftValueIsComplete(value: unknown): boolean {
  if (typeof value === 'boolean') return true;
  return String(value ?? '').trim().length > 0;
}

function localValidationMessage(fieldLabel: string): string {
  switch (fieldLabel) {
    case REQUIRED_FIELD_LABELS.uploadedOriginalFileName:
      return 'Uploaded file is required.';
    case REQUIRED_FIELD_LABELS.clientValidatedComplete:
      return 'Validated must be TRUE.';
    case REQUIRED_FIELD_LABELS.signed:
      return 'Signed must be TRUE or NOT_REQUIRED.';
    case REQUIRED_FIELD_LABELS.approvedBy:
      return 'Approved by is required.';
    case REQUIRED_FIELD_LABELS.approvalDate:
      return 'Approval date is required.';
    case REQUIRED_FIELD_LABELS.intendedOutcome:
      return 'Declared outcome is required.';
    default:
      return `${fieldLabel} is required.`;
  }
}

function validateDocumentDraft(
  document: EvidenceWorkspaceDocument,
  draft: DocumentStatusUpdateRequest,
): string[] {
  const missing: string[] = [];
  if (!hasCompletedUpload(document)) missing.push(REQUIRED_FIELD_LABELS.uploadedOriginalFileName);
  if (!draftValueIsComplete(draft.approvedBy)) missing.push(REQUIRED_FIELD_LABELS.approvedBy);
  if (!draftValueIsComplete(draft.approvalDate)) missing.push(REQUIRED_FIELD_LABELS.approvalDate);
  if (documentRequiresExplicitOutcome(document)) {
    const outcome = String(draft.intendedOutcome || '').trim().toUpperCase();
    if (!outcome || !documentAllowedOutcomes(document).includes(outcome)) {
      missing.push(REQUIRED_FIELD_LABELS.intendedOutcome);
    }
  }
  return missing;
}

function backendValidationIssues(summary: unknown): WorkspaceValidationIssue[] {
  const record = asRecord(summary);
  const structured = asArray(record.documentValidationErrors)
    .map((issue) => ({
      documentId: text(issue, 'documentId') || undefined,
      field: text(issue, 'field') || undefined,
      message: text(issue, 'message') || 'Required field is incomplete.',
    }));
  const rawErrors = Array.isArray(record.validationErrors) ? record.validationErrors : [];
  const parsedRaw = rawErrors
    .map((value) => String(value || '').trim())
    .filter(Boolean)
    .map((message) => {
      const match = message.match(/^([^:]+):\s*(.+)$/);
      return match
        ? { documentId: match[1], message: match[2] }
        : { message };
    });
  const structuredMessages = new Set(structured.map((issue) => `${issue.documentId || ''}:${issue.message}`.toLowerCase()));
  return [
    ...structured,
    ...parsedRaw.filter((issue) => !structuredMessages.has(`${issue.documentId || ''}:${issue.message}`.toLowerCase())),
  ];
}

function apiValidationIssues(error: unknown): WorkspaceValidationIssue[] {
  if (!(error instanceof ApiError)) return [];
  const body = asRecord(error.body);
  const details = asRecord(body.details);
  const structured = asArray(details.documentErrors).map((issue) => ({
    documentId: text(issue, 'documentId') || undefined,
    field: text(issue, 'field') || undefined,
    message: text(issue, 'message') || 'Required field is incomplete.',
  }));
  const errors = Array.isArray(details.errors)
    ? details.errors
    : Array.isArray(body.validationErrors)
      ? body.validationErrors
      : [];
  const parsed = errors
    .map((value) => String(value || '').trim())
    .filter(Boolean)
    .map((message) => {
      const match = message.match(/^([^:]+):\s*(.+)$/);
      return match ? { documentId: match[1], message: match[2] } : { message };
    });
  const structuredMessages = new Set(structured.map((issue) => `${issue.documentId || ''}:${issue.message}`.toLowerCase()));
  return [
    ...structured,
    ...parsed.filter((issue) => !structuredMessages.has(`${issue.documentId || ''}:${issue.message}`.toLowerCase())),
  ];
}

function rowHasRequiredFields(
  document: EvidenceWorkspaceDocument,
  draft: DocumentStatusUpdateRequest,
): boolean {
  return validateDocumentDraft(document, draft).length === 0;
}

function preparedDocumentAvailable(document: EvidenceWorkspaceDocument): boolean {
  const record = document as ApiRecord;
  return Boolean(
    record.preparedDocumentAvailable ||
      document.preparedDocumentKey ||
      document.preparedDocumentFileName ||
      record.preparedDocumentS3Uri
  );
}

function shouldShowDocumentId(document: EvidenceWorkspaceDocument, displayName: string): boolean {
  const docId = String(document.documentId || '');
  return Boolean(docId && docId !== displayName);
}

function openDownloadUrlOnce(downloadUrl: string): void {
  const link = document.createElement('a');
  link.href = downloadUrl;
  link.target = '_blank';
  link.rel = 'noopener noreferrer';
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  link.remove();
}

export default function ClientEvidenceWorkspacePage() {
  const { accountPath } = useClientAccount();
  const api = useMemo(() => createHttpClient({ portal: 'client' }), []);
  const canManageWorkspace = canDownloadClientArtifacts();
  const { openDrawer: openAiAssistant } = useClientAiAssistant();
  const { packageId: routePackageId } = useParams();
  const decodedPackageId = routePackageId ? decodeURIComponent(routePackageId) : '';

  const [state, setState] = useState<LoadState>({ loading: true, packages: [], documents: [] });
  const [selectedFiles, setSelectedFiles] = useState<Record<string, File | undefined>>({});
  const [fileInputResetKeys, setFileInputResetKeys] = useState<Record<string, number>>({});
  const [drafts, setDrafts] = useState<Record<string, DocumentStatusUpdateRequest>>({});
  const [rowSaveStatuses, setRowSaveStatuses] = useState<Record<string, RowSaveStatus>>({});
  const [operation, setOperation] = useState<OperationState>({ kind: 'idle' });
  const [attestation, setAttestation] = useState<ClientAttestationRequest>({
    packageId: decodedPackageId,
    fullName: '',
    title: '',
    company: '',
    email: '',
    typedSignature: '',
    certificationAccepted: false,
  });
  const [attestationStatus, setAttestationStatus] = useState<OperationState>({ kind: 'idle' });
  const [attestationDirty, setAttestationDirty] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<OperationState>({ kind: 'idle' });
  const [submitValidationIssues, setSubmitValidationIssues] = useState<WorkspaceValidationIssue[]>([]);
  const [workspaceMetadata, setWorkspaceMetadata] = useState<GridMetadata | null>(null);
  const [workspacePage, setWorkspacePage] = useState(1);
  const [workspaceFilters, setWorkspaceFilters] = useState<WorkspaceSearchState>({
    search: '',
    searchMode: 'contains',
    status: '',
    cmmcLevel: '',
    limit: DEFAULT_GRID_LIMIT,
  });

  const packageId = decodedPackageId || evidenceWorkspacePackageId(state.workspacePackage ?? ({} as EvidenceWorkspacePackage));

  async function loadWorkspaceList(nextFilters = workspaceFilters, pageNumber = workspacePage) {
    setState((current) => ({ ...current, loading: true, error: undefined }));
    try {
      const response = await api.get<unknown>('/client/evidence-workspace', {
        query: {
          limit: nextFilters.limit,
          page: pageNumber,
          search: nextFilters.search || undefined,
          searchMode: nextFilters.searchMode,
          status: nextFilters.status || undefined,
          cmmcLevel: nextFilters.cmmcLevel || undefined,
        },
      });
      const typedResponse = response as EvidenceWorkspacePackagesResponse;
      setState({
        loading: false,
        packages: normalizeWorkspacePackages(response),
        documents: [],
      });
      setWorkspaceMetadata(metadataFromResponse(typedResponse));
      setWorkspacePage(typedResponse.page ?? pageNumber);
    } catch (error) {
      setState({
        loading: false,
        packages: [],
        documents: [],
        error: error instanceof Error ? error.message : 'Unable to load evidence workspaces.',
      });
      setWorkspaceMetadata(null);
    }
  }

  async function loadWorkspaceDetails(id: string) {
    setState((current) => ({ ...current, loading: true, error: undefined }));
    try {
      const [packageResponse, documentsResponse] = await Promise.all([
        api.get<unknown>(`/client/evidence-workspace/${encodeURIComponent(id)}`),
        api.get<unknown>(`/client/evidence-workspace/${encodeURIComponent(id)}/documents`),
      ]);

      const documents = normalizeWorkspaceDocuments(documentsResponse);
      const workspacePackage = normalizeWorkspacePackage(packageResponse);
      const documentSummary = asRecord((documentsResponse as EvidenceWorkspaceDocumentsResponse).summary);
      if (Object.keys(documentSummary).length > 0) {
        workspacePackage.summary = documentSummary;
      }

      const nextDrafts: Record<string, DocumentStatusUpdateRequest> = {};
      for (const document of documents) {
        nextDrafts[document.documentId] = {
          clientValidatedComplete: 'TRUE',
          signed: 'TRUE',
          approvedBy: document.approvedBy || '',
          approvalDate: document.approvalDate || '',
          intendedOutcome: documentDraftOutcome(document),
          notes: document.notes || '',
        };
      }

      setDrafts(nextDrafts);
      setRowSaveStatuses({});
      const savedAttestation = attestationDraftFromPackage(workspacePackage, id);
      setAttestation(
        savedAttestation ?? {
          packageId: id,
          fullName: '',
          title: '',
          company: '',
          email: '',
          typedSignature: '',
          notes: '',
          certificationAccepted: false,
        },
      );
      setAttestationDirty(false);
      setSubmitValidationIssues([]);
      setState({
        loading: false,
        packages: [],
        workspacePackage,
        documents,
      });
    } catch (error) {
      setState({
        loading: false,
        packages: [],
        documents: [],
        error: error instanceof Error ? error.message : 'Unable to load evidence workspace.',
      });
    }
  }

  useEffect(() => {
    if (decodedPackageId) {
      void loadWorkspaceDetails(decodedPackageId);
    } else {
      void loadWorkspaceList();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, decodedPackageId]);

  function updateDraft(documentId: string, field: keyof DocumentStatusUpdateRequest, value: string | boolean) {
    const normalizedValue = value;
    setDrafts((current) => ({
      ...current,
      [documentId]: {
        ...(current[documentId] ?? {}),
        [field]: normalizedValue,
      },
    }));
    setRowSaveStatuses((current) => ({ ...current, [documentId]: 'dirty' }));
    setSubmitValidationIssues((current) => current.filter((issue) => issue.documentId !== documentId));
  }

  function updateAttestation(patch: Partial<ClientAttestationRequest>) {
    setAttestation((current) => ({ ...current, ...patch }));
    setAttestationDirty(true);
  }

  function updateSelectedFile(documentId: string, event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    setSelectedFiles((current) => ({ ...current, [documentId]: file }));
  }

  async function refreshDocuments() {
    if (!packageId) return;
    const response = await api.get<unknown>(`/client/evidence-workspace/${encodeURIComponent(packageId)}/documents`);
    const documents = normalizeWorkspaceDocuments(response);
    const summary = asRecord((response as EvidenceWorkspaceDocumentsResponse).summary);
    setState((current) => ({
      ...current,
      documents,
      workspacePackage:
        Object.keys(summary).length > 0
          ? ({ ...(current.workspacePackage ?? {}), summary } as EvidenceWorkspacePackage)
          : current.workspacePackage,
    }));
  }

  const preparedDocumentCount = state.documents.filter((document) => preparedDocumentAvailable(document)).length;
  const selectedFileCount = state.documents.filter((document) => Boolean(selectedFiles[document.documentId])).length;

  async function downloadAllPreparedDocuments() {
    if (!packageId || preparedDocumentCount === 0) return;
    if (!canManageWorkspace) {
      setOperation({ kind: 'error', message: 'Template downloads are available only to client administrators.' });
      return;
    }

    setOperation({ kind: 'loading', message: 'Preparing all generated evidence templates for download.' });
    try {
      const response = await api.post<PreparedDocumentsDownloadAllResponse>(
        `/client/evidence-workspace/${encodeURIComponent(packageId)}/download-all-url`,
        { packageId },
      );

      if (!response.downloadUrl) throw new Error('The API did not return a download URL.');

      openDownloadUrlOnce(response.downloadUrl);
      setOperation({
        kind: 'success',
        message: `Download started for ${response.documentCount ?? preparedDocumentCount} generated template(s).`,
      });
    } catch (error) {
      setOperation({
        kind: 'error',
        message: error instanceof Error ? error.message : 'Unable to download all generated templates.',
      });
    }
  }

  async function downloadPreparedDocument(document: EvidenceWorkspaceDocument) {
    if (!packageId || !document.documentId) return;
    if (!canManageWorkspace) {
      setOperation({ kind: 'error', documentId: document.documentId, message: 'Template downloads are available only to client administrators.' });
      return;
    }

    setOperation({ kind: 'loading', documentId: document.documentId, message: 'Preparing secure download URL.' });
    try {
      const response = await api.post<PreparedDocumentDownloadResponse>(
        `/client/evidence-workspace/${encodeURIComponent(packageId)}/documents/${encodeURIComponent(document.documentId)}/download-url`,
        { packageId, documentId: document.documentId },
      );

      if (!response.downloadUrl) throw new Error('The API did not return a download URL.');

      openDownloadUrlOnce(response.downloadUrl);
      setOperation({ kind: 'success', documentId: document.documentId, message: 'Template download started.' });
    } catch (error) {
      setOperation({
        kind: 'error',
        documentId: document.documentId,
        message: error instanceof Error ? error.message : 'Unable to download prepared template.',
      });
    }
  }

  async function uploadSelectedDocumentFile(
    document: EvidenceWorkspaceDocument,
    file: File,
    options?: { refreshAfterUpload?: boolean; successMessage?: string },
  ): Promise<boolean> {
    if (!packageId || !document.documentId) return false;

    setOperation({ kind: 'loading', documentId: document.documentId, message: 'Preparing secure upload URL.' });
    try {
      const uploadResponse = await api.post<CompletedDocumentUploadResponse>(
        `/client/evidence-workspace/${encodeURIComponent(packageId)}/documents/${encodeURIComponent(document.documentId)}/upload-url`,
        {
          packageId,
          documentId: document.documentId,
          fileName: file.name,
          contentType: file.type || 'application/octet-stream',
          fileSizeBytes: file.size,
        },
      );

      if (!uploadResponse.uploadUrl) throw new Error('The API did not return an upload URL.');

      const requiredHeaders = uploadResponse.requiredHeaders ?? {};
      const uploadResult = await fetch(uploadResponse.uploadUrl, {
        method: uploadResponse.method || 'PUT',
        headers: requiredHeaders,
        body: file,
      });

      if (!uploadResult.ok) {
        throw new Error(`Upload failed with status ${uploadResult.status}.`);
      }

      setSelectedFiles((current) => ({ ...current, [document.documentId]: undefined }));
      setFileInputResetKeys((current) => ({
        ...current,
        [document.documentId]: (current[document.documentId] ?? 0) + 1,
      }));
      setRowSaveStatuses((current) => ({ ...current, [document.documentId]: 'dirty' }));
      if (options?.refreshAfterUpload !== false) {
        await refreshDocuments();
      }
      setOperation({
        kind: 'success',
        documentId: document.documentId,
        message: options?.successMessage || 'Document uploaded. Complete required fields, then save the row.',
      });
      return true;
    } catch (error) {
      setOperation({
        kind: 'error',
        documentId: document.documentId,
        message: error instanceof Error ? error.message : 'Unable to upload completed document.',
      });
      return false;
    }
  }

  async function uploadCompletedDocument(document: EvidenceWorkspaceDocument) {
    if (!document.documentId) return;
    const file = selectedFiles[document.documentId];

    if (!file) {
      setOperation({ kind: 'error', documentId: document.documentId, message: 'Select a file before uploading.' });
      return;
    }

    await uploadSelectedDocumentFile(document, file);
  }

  async function uploadAllSelectedDocuments() {
    if (!packageId) return;

    const documentsWithFiles = state.documents.filter((document) => Boolean(selectedFiles[document.documentId]));
    if (documentsWithFiles.length === 0) {
      setOperation({ kind: 'warning', message: 'Select one or more completed files before using Upload all documents.' });
      return;
    }

    setOperation({ kind: 'loading', message: `Uploading ${documentsWithFiles.length} selected document(s).` });
    let uploadedCount = 0;
    for (const document of documentsWithFiles) {
      const file = selectedFiles[document.documentId];
      if (!file) continue;
      // eslint-disable-next-line no-await-in-loop
      const uploaded = await uploadSelectedDocumentFile(document, file, {
        refreshAfterUpload: false,
        successMessage: `Uploaded ${uploadedCount + 1} of ${documentsWithFiles.length} selected document(s).`,
      });
      if (!uploaded) {
        setOperation({
          kind: 'error',
          documentId: document.documentId,
          message: `Upload all stopped at ${documentDisplayName(document)}. Correct the row and try again.`,
        });
        return;
      }
      uploadedCount += 1;
    }

    await refreshDocuments();
    setOperation({ kind: 'success', message: `Uploaded ${uploadedCount} selected document(s). Complete required fields, then save the rows.` });
  }

  async function saveDocumentStatus(document: EvidenceWorkspaceDocument): Promise<boolean> {
    if (!packageId || !document.documentId) return false;
    const draft = {
      ...(drafts[document.documentId] ?? {}),
      clientValidatedComplete: 'TRUE' as const,
      signed: 'TRUE' as const,
    };
    const missing = validateDocumentDraft(document, draft);

    setOperation({ kind: 'loading', documentId: document.documentId, message: 'Saving document row.' });
    setRowSaveStatuses((current) => ({ ...current, [document.documentId]: 'saving' }));
    try {
      const response = await api.patch<DocumentStatusUpdateResponse>(
        `/client/evidence-workspace/${encodeURIComponent(packageId)}/documents/${encodeURIComponent(document.documentId)}`,
        draft,
      );

      setState((current) => ({
        ...current,
        documents: current.documents.map((item) =>
          item.documentId === document.documentId ? (response.document ?? item) : item,
        ),
        workspacePackage: response.summary
          ? ({ ...(current.workspacePackage ?? {}), summary: response.summary } as EvidenceWorkspacePackage)
          : current.workspacePackage,
      }));
      if (response.document) {
        setDrafts((current) => ({
          ...current,
          [document.documentId]: {
            clientValidatedComplete: 'TRUE',
            signed: 'TRUE',
            approvedBy: response.document.approvedBy || '',
            approvalDate: response.document.approvalDate || '',
            intendedOutcome: documentDraftOutcome(response.document),
            notes: response.document.notes || '',
          },
        }));
      }
      setSubmitValidationIssues([]);
      setRowSaveStatuses((current) => ({ ...current, [document.documentId]: 'saved' }));
      setOperation({
        kind: missing.length > 0 ? 'warning' : 'success',
        documentId: document.documentId,
        message: missing.length > 0
          ? `Document row saved. Remaining required field(s): ${missing.join(', ')}.`
          : 'Document row saved.',
      });
      return true;
    } catch (error) {
      setRowSaveStatuses((current) => ({ ...current, [document.documentId]: 'error' }));
      setOperation({
        kind: 'error',
        documentId: document.documentId,
        message: error instanceof Error ? error.message : 'Unable to save document row.',
      });
      return false;
    }
  }

  async function saveAllDocuments() {
    if (!packageId) return;

    setOperation({ kind: 'loading', message: 'Saving all document rows.' });
    let savedCount = 0;
    for (const document of state.documents) {
      // eslint-disable-next-line no-await-in-loop
      const saved = await saveDocumentStatus(document);
      if (!saved) {
        setOperation({
          kind: 'error',
          documentId: document.documentId,
          message: `Save all stopped at ${documentDisplayName(document)}. Correct the row and try again.`,
        });
        return;
      }
      savedCount += 1;
    }
    setOperation({ kind: 'success', message: `Saved ${savedCount} document row(s).` });
  }

  async function saveAttestation(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    if (!packageId) return;
    if (!attestation.certificationAccepted) {
      setAttestationStatus({ kind: 'error', message: 'You must accept the certification statement before saving.' });
      return;
    }

    setAttestationStatus({ kind: 'loading', message: 'Saving attestation.' });
    try {
      const response = await api.post<ClientAttestationResponse>(
        `/client/evidence-workspace/${encodeURIComponent(packageId)}/attestation`,
        { ...attestation, packageId },
      );

      const responseRecord = asRecord(response);
      const returnedPackage = (response.workspacePackage || responseRecord.package || state.workspacePackage || {}) as EvidenceWorkspacePackage;
      const normalizedPackage = normalizeWorkspacePackage({
        workspacePackage: returnedPackage,
        summary: responseRecord.summary,
        attestation: responseRecord.attestation,
      });
      const savedAttestation = attestationDraftFromPackage(normalizedPackage, packageId);

      setState((current) => ({
        ...current,
        workspacePackage: normalizedPackage ?? current.workspacePackage,
      }));
      if (savedAttestation) {
        setAttestation(savedAttestation);
      }
      setAttestationDirty(false);
      setSubmitValidationIssues([]);
      setAttestationStatus({ kind: 'success', message: response.message || 'Attestation saved.' });
    } catch (error) {
      setAttestationStatus({
        kind: 'error',
        message: error instanceof Error ? error.message : 'Unable to save attestation.',
      });
    }
  }

  async function submitWorkspace() {
    if (!packageId) return;

    // Submission validation is authoritative on the API. Keep the button usable so
    // the client can request a fresh validation result instead of being trapped by
    // stale or duplicate browser-side validation state.
    setSubmitValidationIssues([]);
    setSubmitStatus({ kind: 'loading', message: 'Validating and submitting evidence workspace.' });
    try {
      const response = await api.post<SubmitEvidenceWorkspaceResponse>(
        `/client/evidence-workspace/${encodeURIComponent(packageId)}/submit`,
        { packageId, confirmSubmit: true },
      );

      if (response.validationPassed === false) {
        const responseIssues = (response.validationErrors || []).map((message) => {
          const match = message.match(/^([^:]+):\s*(.+)$/);
          return match ? { documentId: match[1], message: match[2] } : { message };
        });
        if (responseIssues.length > 0) setSubmitValidationIssues(responseIssues);
        const validationErrors = response.validationErrors?.join(' ');
        setSubmitStatus({
          kind: 'error',
          message: validationErrors || response.message || 'Workspace validation failed.',
        });
        return;
      }

      setState((current) => ({
        ...current,
        workspacePackage: {
          ...(current.workspacePackage ?? {}),
          packageId,
          workspaceLocked: true,
          workspaceStatus: response.workspaceStatus || 'COMPLETED_EVIDENCE_PACKAGE_BUILD_REQUESTED',
          packageStatus: response.workspaceStatus || (current.workspacePackage as EvidenceWorkspacePackage | undefined)?.packageStatus,
          summary: response.summary,
        } as EvidenceWorkspacePackage,
      }));
      setSubmitStatus({ kind: 'success', message: response.message || 'Final auditor package generation was requested.' });
      window.location.assign(accountPath('/evidence-packages'));
    } catch (error) {
      const validationIssues = apiValidationIssues(error);
      if (validationIssues.length > 0) setSubmitValidationIssues(validationIssues);
      setSubmitStatus({
        kind: 'error',
        message: error instanceof Error ? error.message : 'Unable to submit evidence workspace.',
      });
    }
  }

  if (state.loading) {
    return (
      <div className="page-stack">
        <div className="page-heading">
          <h2>Evidence Workspace</h2>
          <p>Loading client evidence workspace.</p>
        </div>
      </div>
    );
  }

  if (state.error) {
    return (
      <div className="page-stack">
        <div className="page-heading">
          <h2>Evidence Workspace</h2>
          <p className="error-text">{state.error}</p>
        </div>
      </div>
    );
  }

  if (!decodedPackageId) {
    return (
      <div className="page-stack">
        <div className="page-heading">
          <h2>Evidence Workspace</h2>
          <p>{canManageWorkspace ? 'Select a prepared evidence workspace to download starter documents, upload completed files, and submit the final client evidence package.' : 'Select a prepared evidence workspace to review status and package details. Downloads and updates are available only to client administrators.'}</p>
        </div>

        <div className="card">
          <div className="section-title-row">
            <div>
              <h3>Available workspaces</h3>
            </div>
            <button className="secondary-button" type="button" onClick={() => void loadWorkspaceList()}>
              Refresh
            </button>
          </div>

          <form
            className="filter-grid"
            onSubmit={(event) => {
              event.preventDefault();
              setWorkspacePage(1);
              void loadWorkspaceList(workspaceFilters, 1);
            }}
          >
            <label>
              Search
              <input
                value={workspaceFilters.search}
                onChange={(event) => setWorkspaceFilters((current) => ({ ...current, search: event.target.value }))}
                placeholder="Workspace, package, job, status, or level"
              />
            </label>
            <label>
              Match type
              <select value={workspaceFilters.searchMode} onChange={(event) => setWorkspaceFilters((current) => ({ ...current, searchMode: event.target.value as SearchMode }))}>
                <option value="contains">Contains</option>
                <option value="exact">Exact word</option>
                <option value="beginsWith">Begins with</option>
              </select>
            </label>
            <label>
              Status
              <select value={workspaceFilters.status} onChange={(event) => setWorkspaceFilters((current) => ({ ...current, status: event.target.value }))}>
                {WORKSPACE_STATUS_OPTIONS.map((status) => (
                  <option key={status || 'all'} value={status}>{status ? friendlyStatus(status) : 'All statuses'}</option>
                ))}
              </select>
            </label>
            <label>
              CMMC Level
              <select value={workspaceFilters.cmmcLevel} onChange={(event) => setWorkspaceFilters((current) => ({ ...current, cmmcLevel: event.target.value }))}>
                {CMMC_LEVEL_OPTIONS.map((level) => (
                  <option key={level || 'all'} value={level}>{level || 'All levels'}</option>
                ))}
              </select>
            </label>
            <label>
              Limit
              <select value={workspaceFilters.limit} onChange={(event) => setWorkspaceFilters((current) => ({ ...current, limit: Number(event.target.value) }))}>
                {GRID_LIMIT_OPTIONS.map((limit) => (
                  <option key={limit} value={limit}>{limit}</option>
                ))}
              </select>
            </label>
            <div className="form-actions filter-actions">
              <button className="primary-button" type="submit">Search</button>
              <button
                className="secondary-button"
                type="button"
                onClick={() => {
                  const cleared: WorkspaceSearchState = { search: '', searchMode: 'contains', status: '', cmmcLevel: '', limit: DEFAULT_GRID_LIMIT };
                  setWorkspaceFilters(cleared);
                  setWorkspacePage(1);
                  void loadWorkspaceList(cleared, 1);
                }}
              >
                Clear
              </button>
            </div>
          </form>

          {workspaceMetadata && (
            <div className="grid-header-row compact-grid-header">
              <span className="result-count">Showing {state.packages.length} of {workspaceMetadata.matchedCount ?? state.packages.length} matched · Page {workspaceMetadata.page ?? workspacePage}{workspaceMetadata.pageCount ? ` of ${workspaceMetadata.pageCount}` : ''}</span>
              <div className="pagination-row">
                <button className="secondary-button" type="button" disabled={!workspaceMetadata.hasPreviousPage} onClick={() => void loadWorkspaceList(workspaceFilters, Math.max(1, workspacePage - 1))}>Previous</button>
                <button className="secondary-button" type="button" disabled={!workspaceMetadata.hasNextPage} onClick={() => void loadWorkspaceList(workspaceFilters, workspacePage + 1)}>Next</button>
              </div>
            </div>
          )}

          {state.packages.length === 0 ? (
            <p className="muted-text">No evidence workspaces are currently available.</p>
          ) : (
            <div className="table-scroll">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Workspace</th>
                    <th>Status</th>
                    <th>CMMC Level</th>
                    <th>Updated</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {state.packages.map((pkg) => {
                    const id = evidenceWorkspacePackageId(pkg);
                    const pkgLocked = isWorkspaceLocked(pkg);
                    return (
                      <tr key={id} className={pkgLocked ? 'package-row-closed' : undefined}>
                        <td>
                          <strong>{evidenceWorkspacePackageName(pkg)}</strong>
                          {shouldShowWorkspacePackageId(pkg) && (
                            <>
                              <br />
                              <span className="muted-text">{id}</span>
                            </>
                          )}
                        </td>
                        <td><span className={statusClass(pkg.workspaceStatus || pkg.packageStatus)}>{evidenceWorkspaceStatus(pkg)}</span></td>
                        <td>{valueOrDash(pkg.cmmcLevel)}</td>
                        <td>{formatLocalDateTime(String(pkg.updatedAt || pkg.createdAt || ''))}</td>
                        <td>
                          <Link
                            className={`secondary-button compact-action-button ${pkgLocked ? 'readonly-workspace-button' : ''}`}
                            to={accountPath(`/evidence-workspace/${encodeURIComponent(id)}`)}
                            title={pkgLocked ? 'Open this closed workspace in read-only mode' : 'Open workspace'}
                          >
                            Open Workspace
                          </Link>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    );
  }

  const workspacePackage = state.workspacePackage;
  const workspaceLocked = isWorkspaceLocked(workspacePackage);
  const workspaceBuildFailed = isWorkspaceBuildFailed(workspacePackage);
  const workspaceReadOnly = workspaceLocked || !canManageWorkspace;
  const localUploadedDocuments = state.documents.filter(hasCompletedUpload).length;
  const localDocumentIssues = state.documents.flatMap((document) =>
    validateDocumentDraft(document, drafts[document.documentId] ?? {}).map((field) => ({
      documentId: document.documentId,
      field,
      message: localValidationMessage(field),
    })),
  );
  const localMissingRequiredDocuments = new Set(localDocumentIssues.map((issue) => issue.documentId)).size;
  const unsavedDocumentIds = new Set(
    state.documents
      .filter((document) => ['dirty', 'saving', 'error'].includes(rowSaveStatuses[document.documentId] ?? 'idle'))
      .map((document) => document.documentId),
  );
  const attestationMissingFields = [
    !attestation.fullName.trim() ? 'Full name' : '',
    !String(attestation.title || '').trim() ? 'Title or role' : '',
    !String(attestation.company || '').trim() ? 'Company' : '',
    !attestation.email.trim() ? 'Email' : '',
    !attestation.certificationAccepted ? 'Certification acceptance' : '',
  ].filter(Boolean);
  const attestationSaved = Boolean(workspacePackage?.clientAttested) && !attestationDirty;
  const backendIssues = submitValidationIssues;
  const currentValidationIssues: WorkspaceValidationIssue[] = [
    ...localDocumentIssues,
    ...Array.from(unsavedDocumentIds).map((documentId) => ({ documentId, message: 'Save this row before submitting.' })),
    ...backendIssues.filter((issue) => {
      if (!issue.documentId) return !issue.message.toLowerCase().includes('attestation');
      return !localDocumentIssues.some((localIssue) => localIssue.documentId === issue.documentId && localIssue.field === issue.field);
    }),
  ];
  if (!attestationSaved) {
    currentValidationIssues.push({
      message: attestationMissingFields.length > 0
        ? `Final client attestation is incomplete: ${attestationMissingFields.join(', ')}.`
        : 'Save the final client attestation before submitting.',
    });
  }
  const displayedValidationIssues = Array.from(
    new Map(
      currentValidationIssues.map((issue) => [
        issue.field
          ? `${issue.documentId || ''}|${issue.field}`
          : `${issue.documentId || ''}|${issue.message}`,
        issue,
      ]),
    ).values(),
  );
  const visibleValidationIssues = displayedValidationIssues.slice(0, 12);
  const hiddenValidationIssueCount = Math.max(0, displayedValidationIssues.length - visibleValidationIssues.length);
  const offendingDocumentIds = new Set(
    displayedValidationIssues.map((issue) => issue.documentId).filter((value): value is string => Boolean(value)),
  );
  const totalDocuments = metricValue(workspacePackage, 'totalDocuments', state.documents.length);
  const uploadedDocuments = metricValue(workspacePackage, 'uploadedDocuments', localUploadedDocuments);
  const missingRequiredDocuments = localMissingRequiredDocuments;
  const readyForSubmission = state.documents.length > 0 && displayedValidationIssues.length === 0;
  const workspacePackageNameForAi = evidenceWorkspacePackageName(workspacePackage ?? ({} as EvidenceWorkspacePackage));
  const workspacePackageTypeForAi = text((workspacePackage ?? {}) as ApiRecord, 'packageType', 'type') || 'TECHNICAL_EVIDENCE_PACKAGE';

  return (
    <div className="page-stack">
      <div className="page-heading page-heading-with-actions">
        <div>
          <h2>Evidence Workspace</h2>
          <p>{canManageWorkspace ? 'Complete prepared evidence documents, upload signed files, and submit the final client evidence package.' : 'Read-only view. You can review this workspace, but downloads and updates are available only to client administrators.'}</p>
        </div>
        <div className="card-actions">
          <Link className="secondary-button" to={accountPath('/evidence-workspace')}>Back to workspaces</Link>
          <Link className="secondary-button" to={accountPath(`/evidence-packages/${encodeURIComponent(decodedPackageId)}`)}>
            View package detail
          </Link>
        </div>
      </div>

      <div className="card">
        <div className="section-title-row">
          <div>
            <h3>{evidenceWorkspacePackageName(workspacePackage ?? ({} as EvidenceWorkspacePackage))}</h3>
            {decodedPackageId !== evidenceWorkspacePackageName(workspacePackage ?? ({} as EvidenceWorkspacePackage)) && (
              <p className="muted-text">{decodedPackageId}</p>
            )}
          </div>
          <span className={statusClass(workspacePackage?.workspaceStatus || workspacePackage?.packageStatus)}>
            {evidenceWorkspaceStatus(workspacePackage)}
          </span>
        </div>

        <div className="metric-grid">
          <div className="metric-card metric-blue">
            <span>Total documents</span>
            <strong>{totalDocuments}</strong>
            <small>Prepared evidence rows</small>
          </div>
          <div className="metric-card metric-success">
            <span>Uploaded</span>
            <strong>{uploadedDocuments}</strong>
            <small>Completed files received</small>
          </div>
          <div className="metric-card metric-warning">
            <span>Missing required</span>
            <strong>{missingRequiredDocuments}</strong>
            <small>Must be resolved before submit</small>
          </div>
          <div className="metric-card">
            <span>Submit ready</span>
            <strong>{readyForSubmission ? 'Yes' : 'No'}</strong>
            <small>Current required fields and save state</small>
          </div>
        </div>
      </div>

      {workspaceBuildFailed && (() => {
        const failureGuidance = workspaceBuildFailureGuidance(workspacePackage);
        return (
          <div className="card workspace-build-error-banner" role="alert">
            <h3>Completed evidence package generation failed</h3>
            <p>{failureGuidance.message}</p>
            {workspacePackage?.workspaceBuildFailedAt ? (
              <p className="muted-text">Failed {formatLocalDateTime(String(workspacePackage.workspaceBuildFailedAt))}</p>
            ) : null}
            {failureGuidance.contactSupport ? (
              <p>Contact SURELINC Support, then submit the package again after the issue is resolved.</p>
            ) : (
              <p>The workspace is available again. Confirm the corrected evidence and submit the completed package again.</p>
            )}
          </div>
        );
      })()}

      {workspaceLocked && (
        <div className="card closed-workspace-banner">
          <h3>Workspace closed</h3>
          <p>{workspaceClosedMessage(workspacePackage)}</p>
        </div>
      )}

      {!canManageWorkspace && (
        <div className="card role-access-banner">
          <h3>View-only access</h3>
          <p>You can review this workspace safely. Downloads, uploads, edits, attestations, and submission are available only to client administrators.</p>
        </div>
      )}

      <div className="card">
        <div className="section-title-row">
          <div>
            <h3>Template completion grid</h3>
            <p className="muted-text">Upload completed files and update only the required client approval fields.</p>
          </div>
          <div className="workspace-grid-toolbar">
            <button
              className="primary-button"
              type="button"
              disabled={workspaceReadOnly || operation.kind === 'loading' || preparedDocumentCount === 0}
              onClick={() => void downloadAllPreparedDocuments()}
              title={!canManageWorkspace ? 'Downloads are available only to client administrators.' : preparedDocumentCount > 0 ? 'Download all generated evidence templates as one ZIP file' : 'Generated evidence templates are not available yet'}
            >
              Download all templates
            </button>
            <button
              className="primary-button"
              type="button"
              disabled={workspaceReadOnly || operation.kind === 'loading'}
              onClick={() => void uploadAllSelectedDocuments()}
              title={selectedFileCount > 0 ? `Upload ${selectedFileCount} selected completed document(s)` : 'Select completed files in the grid before uploading all'}
            >
              Upload all documents
            </button>
            <button
              className="primary-button"
              type="button"
              disabled={workspaceReadOnly || operation.kind === 'loading' || state.documents.length === 0}
              onClick={() => void saveAllDocuments()}
            >
              Save all rows
            </button>
          </div>
        </div>

        {operation.kind !== 'idle' && operation.message && (
          <p className={operation.kind === 'error' ? 'error-text' : operation.kind === 'warning' ? 'warning-text' : operation.kind === 'success' ? 'success-text' : 'muted-text'}>
            {operation.message}
          </p>
        )}

        <div className="table-scroll">
          <table className="data-table workspace-grid-table">
            <colgroup>
              <col className="workspace-document-col" />
              <col className="workspace-upload-col" />
              <col className="workspace-approved-col" />
              <col className="workspace-outcome-col" />
              <col className="workspace-date-col" />
              <col className="workspace-notes-col" />
              <col className="workspace-actions-col" />
            </colgroup>
            <thead>
              <tr>
                <th>{requiredLabel('Templates')}</th>
                <th>{requiredLabel('Completed upload')}</th>
                <th>{requiredLabel('Approved by')}</th>
                <th>Declared outcome</th>
                <th>{requiredLabel('Approval date')}</th>
                <th>Notes</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {state.documents.map((document) => {
                const docId = document.documentId;
                const draft = drafts[docId] ?? {};
                const busy = operation.kind === 'loading' && operation.documentId === docId;
                const displayName = documentDisplayName(document);
                const rowSaveStatus = rowSaveStatuses[docId] ?? 'idle';
                const missingRequiredFields = validateDocumentDraft(document, draft);
                const rowComplete = rowHasRequiredFields(document, draft);
                const rowValidationMessages = displayedValidationIssues
                  .filter((issue) => issue.documentId === docId)
                  .map((issue) => issue.message);
                const rowOffending = !rowComplete || offendingDocumentIds.has(docId) || unsavedDocumentIds.has(docId);
                return (
                  <tr key={docId} className={rowOffending ? 'workspace-row-incomplete' : undefined}>
                    <td className="workspace-document-cell">
                      <span className="workspace-doc-title">
                        {displayName}
                      </span>
                      {document.required === false && (
                        <span className="status-pill status-pill-muted">Optional</span>
                      )}
                      <div className="workspace-template-actions">
                        <button
                          className="primary-button compact-action-button workspace-prepared-download-button workspace-document-download-button"
                          type="button"
                          disabled={workspaceReadOnly || busy || !preparedDocumentAvailable(document)}
                          onClick={() => void downloadPreparedDocument(document)}
                          title={!canManageWorkspace ? 'Downloads are available only to client administrators.' : preparedDocumentAvailable(document) ? 'Download prepared evidence template' : 'Prepared evidence template is not available yet'}
                        >
                          {busy ? 'Preparing...' : 'Download template'}
                        </button>
                        <button
                          className="secondary-button compact-action-button ai-row-explain-button workspace-template-explain-button"
                          type="button"
                          disabled={!docId}
                          onClick={() => openAiAssistant(
                            {
                              packageId,
                              selectedPackageId: packageId,
                              packageName: workspacePackageNameForAi,
                              packageType: workspacePackageTypeForAi,
                              documentId: docId,
                              selectedDocumentId: docId,
                              documentTitle: displayName,
                              pageType: 'EVIDENCE_WORKSPACE',
                              sourceLabel: 'Evidence Workspace document row',
                              pageContext: {
                                pageType: 'EVIDENCE_WORKSPACE',
                                packageId,
                                packageName: workspacePackageNameForAi,
                                packageType: workspacePackageTypeForAi,
                                documentId: docId,
                                documentTitle: displayName,
                                sourceLabel: 'Evidence Workspace document row',
                              },
                            },
                            buildDocumentAiPrompt(document, displayName),
                            {
                              autoSubmit: true,
                              displayMessage: `What is ${displayName} and what is needed to fill it out?`,
                            },
                          )}
                          title="Ask the SURELINC Readiness AI Assistant™ to explain this prepared document"
                        >
                          Explain
                        </button>
                      </div>
                    </td>
                    <td className="workspace-upload-cell">
                      <div className="workspace-upload-status-row">
                        <span className={statusClass(hasCompletedUpload(document) ? 'UPLOADED' : 'PENDING')}>
                          {completedUploadLabel(document)}
                        </span>
                      </div>
                      {document.uploadedOriginalFileName && (
                        <span className="workspace-upload-file-name">Current file: {document.uploadedOriginalFileName}</span>
                      )}
                      <div className="workspace-upload-actions">
                        <input
                          key={`${docId}-${fileInputResetKeys[docId] ?? 0}`}
                          aria-label={`Upload completed file for ${displayName}`}
                          type="file"
                          accept=".docx,.xlsx,.pdf,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                          disabled={workspaceReadOnly}
                          onChange={(event) => updateSelectedFile(docId, event)}
                        />
                        <button
                          className="primary-button compact-action-button workspace-upload-button"
                          type="button"
                          disabled={workspaceReadOnly || busy}
                          onClick={() => void uploadCompletedDocument(document)}
                        >
                          Upload
                        </button>
                      </div>
                      {missingRequiredFields.length > 0 && (
                        <span className="workspace-missing-fields workspace-missing-fields-inline">Missing: {missingRequiredFields.join(', ')}</span>
                      )}
                      {rowValidationMessages.length > 0 && (
                        <span className="workspace-validation-errors-inline">
                          {Array.from(new Set(rowValidationMessages)).join(' ')}
                        </span>
                      )}
                    </td>
                    <td className="workspace-approved-cell">
                      <select disabled={workspaceReadOnly} value={String(draft.approvedBy ?? '')} onChange={(event) => updateDraft(docId, 'approvedBy', event.target.value)}>
                        <option value="">Select</option>
                        {APPROVED_BY_OPTIONS.map((option) => <option key={option} value={option}>{option}</option>)}
                      </select>
                    </td>
                    <td className="workspace-select-cell">
                      {documentRequiresExplicitOutcome(document) ? (
                        <select disabled={workspaceReadOnly} value={String(draft.intendedOutcome ?? '')} onChange={(event) => updateDraft(docId, 'intendedOutcome', event.target.value)}>
                          <option value="">Select</option>
                          {documentAllowedOutcomes(document).map((option) => <option key={option} value={option}>{friendlyStatus(option)}</option>)}
                        </select>
                      ) : (
                        <span className="muted-text">—</span>
                      )}
                    </td>
                    <td className="workspace-date-cell">
                      <input
                        type="date"
                        disabled={workspaceReadOnly}
                        value={String(draft.approvalDate ?? '')}
                        onChange={(event) => updateDraft(docId, 'approvalDate', event.target.value)}
                      />
                    </td>
                    <td className="workspace-notes-cell">
                      <textarea
                        rows={3}
                        disabled={workspaceReadOnly}
                        value={String(draft.notes ?? '')}
                        onChange={(event) => updateDraft(docId, 'notes', event.target.value)}
                      />
                    </td>
                    <td className="workspace-actions-cell">
                      <div className="workspace-row-actions">
                        <button
                          className={`primary-button compact-action-button workspace-save-row-button ${rowSaveStatus === 'saved' ? 'workspace-save-row-button-saved' : ''} ${rowSaveStatus === 'error' ? 'danger-button' : ''}`}
                          type="button"
                          disabled={workspaceReadOnly || busy}
                          onClick={() => void saveDocumentStatus(document)}
                        >
                          {rowSaveStatus === 'saving' ? 'Saving...' : rowSaveStatus === 'saved' ? 'Saved' : 'Save row'}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div className="card">
        <div className="section-title-row">
          <div>
            <h3>Final client attestation</h3>
            <p className="muted-text">Save the signer information before submitting the completed evidence workspace.</p>
          </div>
        </div>

        <form className="form-grid" onSubmit={(event) => void saveAttestation(event)}>
          <label>
            Full name
            <input
              required
              disabled={workspaceReadOnly}
              value={attestation.fullName}
              onChange={(event) => updateAttestation({ fullName: event.target.value })}
            />
          </label>
          <label>
            Title or role
            <input
              required
              disabled={workspaceReadOnly}
              value={attestation.title || ''}
              onChange={(event) => updateAttestation({ title: event.target.value, role: event.target.value })}
            />
          </label>
          <label>
            Company
            <input
              required
              disabled={workspaceReadOnly}
              value={attestation.company || ''}
              onChange={(event) => updateAttestation({ company: event.target.value })}
            />
          </label>
          <label>
            Email
            <input
              required
              type="email"
              disabled={workspaceReadOnly}
              value={attestation.email}
              onChange={(event) => updateAttestation({ email: event.target.value })}
            />
          </label>
          <label>
            Typed signature
            <input
              disabled={workspaceReadOnly}
              value={attestation.typedSignature || ''}
              onChange={(event) => updateAttestation({ typedSignature: event.target.value })}
            />
          </label>
          <label>
            Notes
            <textarea
              rows={3}
              disabled={workspaceReadOnly}
              value={attestation.notes || ''}
              onChange={(event) => updateAttestation({ notes: event.target.value })}
            />
          </label>
          <label>
            <span>
              <input
                checked={attestation.certificationAccepted}
                disabled={workspaceReadOnly}
                type="checkbox"
                onChange={(event) => updateAttestation({ certificationAccepted: event.target.checked })}
              />{' '}
              I certify that the uploaded documents and responses are complete, accurate, approved by the appropriate client representative, and ready for inclusion in the CMMC evidence package.
            </span>
          </label>

          <div className="form-actions">
            <button className="primary-button" type="submit" disabled={workspaceReadOnly || attestationStatus.kind === 'loading'}>
              Save attestation
            </button>
            {attestationStatus.message && (
              <span className={attestationStatus.kind === 'error' ? 'error-text' : attestationStatus.kind === 'success' ? 'success-text' : 'muted-text'}>
                {attestationStatus.message}
              </span>
            )}
          </div>
        </form>
      </div>

      <div className="card">
        <div className="section-title-row">
          <div>
            <h3>Submit completed evidence package</h3>
            <p className="muted-text">Submission validates the workspace and requests final auditor package generation. PDF conversion is performed by the backend processor, not by the portal.</p>
          </div>
        </div>

        {displayedValidationIssues.length > 0 ? (
          <div className="workspace-validation-summary" role="alert" aria-live="polite">
            <h4>Submission checks remaining ({displayedValidationIssues.length})</h4>
            <p>You may continue uploading and saving. Submission will run the authoritative backend validation.</p>
            <ul>
              {visibleValidationIssues.map((issue, index) => {
                const document = issue.documentId
                  ? state.documents.find((item) => item.documentId === issue.documentId)
                  : undefined;
                const label = document ? documentDisplayName(document) : '';
                return (
                  <li key={`${issue.documentId || 'workspace'}-${issue.field || index}-${index}`}>
                    {label ? <strong>{label}: </strong> : null}
                    {issue.message}
                  </li>
                );
              })}
            </ul>
            {hiddenValidationIssueCount > 0 ? (
              <p className="muted-text">{hiddenValidationIssueCount} additional issue(s) are shown in their affected rows.</p>
            ) : null}
          </div>
        ) : (
          <div className="workspace-validation-ready" role="status">
            All required document fields and the final client attestation are saved. This workspace is ready to submit.
          </div>
        )}

        <div className="form-actions">
          <button
            className="primary-button"
            type="button"
            disabled={workspaceReadOnly || submitStatus.kind === 'loading'}
            onClick={() => void submitWorkspace()}
            title={!readyForSubmission ? 'Run submission validation and review any remaining issues.' : 'Submit the completed evidence package'}
          >
            Submit Completed Evidence Package
          </button>
          {submitStatus.message && (
            <span className={submitStatus.kind === 'error' ? 'error-text' : submitStatus.kind === 'success' ? 'success-text' : 'muted-text'}>
              {submitStatus.message}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
